import { useMemo } from 'react'

import {
  digObject, matchFilterArrayIncludes, matchFilterNumber, matchFilterString, sortArrayBy,
} from '@domain-group/javascript-utils'

import { useLoadMore, useWatchEntityUpdates } from '@domain-group/react-hooks'

import useReduxAction from '@hooks/useReduxAction'
import useSelector from '@hooks/useSelector'

import { ModuleState } from '@redux/modules/module'

import { BrandGroupRequestOptions } from '@models/brandGroup'

const watchEntityKeys = ['brandGroups']

type BrandGroupFilters = {
  agency_id?: number,
  brand_id?: number,
  provider_id?: number,
  publication_id?: number,
  string?: string,
}

type Options = {
  filters?: BrandGroupFilters,
  performHttpRequests?: boolean,
  skipLoadedForKeysCheck?: boolean,
  requestOptions?: BrandGroupRequestOptions,
}

function useBrandGroups(options: Options = {}) {
  const { filters, requestOptions, skipLoadedForKeysCheck } = options

  const {
    agency_id: filterAgencyId,
    brand_id: filterBrandId,
    provider_id: filterProviderId,
    publication_id: filterPublicationId,
    string,
  } = filters || {}

  const {
    updatedEntities: { brandGroups: brandGroupsUpdatedAt },
  } = useWatchEntityUpdates(watchEntityKeys)

  const brandGroups = useSelector(reduxState => reduxState.entities.brandGroups)

  const filteredBrandGroups = useMemo(() => {
    const filtered = Object.values(brandGroups).filter((brandGroup) => {
      const { brand_id, id, title } = brandGroup

      const agencyIds = digObject(brandGroup, 'member_ids', [])
      const providerIds = digObject(brandGroup, 'provider_ids', [])
      const publicationIds = digObject(brandGroup, 'publication_ids', [])

      const matchAgency = matchFilterArrayIncludes(agencyIds, Number(filterAgencyId))
      const matchBrand = matchFilterNumber(brand_id, filterBrandId)
      const matchId = id.toString() === string
      const matchProvider = matchFilterArrayIncludes(providerIds, Number(filterProviderId))
      const matchPublication = matchFilterArrayIncludes(publicationIds, Number(filterPublicationId))
      const matchTitle = matchFilterString(title, string)
      const matchString = matchId || matchTitle

      return matchAgency
        && matchBrand
        && matchProvider
        && matchPublication
        && matchString
    })

    return sortArrayBy(filtered, 'asc', 'title')
  }, [brandGroupsUpdatedAt, JSON.stringify(filters)])

  const hasFilteredBrandGroups = !!filteredBrandGroups.length
  const filteredBrandGroupsCount = filteredBrandGroups.length

  const loadMorePayload = useLoadMore({
    filters,
    loadedCount: filteredBrandGroupsCount,
    performHttpRequests: options.performHttpRequests,
  })

  const {
    callbacks: { loadMore },
    canLoadMore,
    filtersWithOffset,
    limit,
    performHttpRequests,
  } = loadMorePayload

  const { loading: loadingBrandGroups } = useReduxAction(
    'brandGroups',
    'loadBrandGroups',
    {
      ...filtersWithOffset,
      ...requestOptions,
      limit,
    },
    [performHttpRequests, filtersWithOffset],
    {
      shouldPerformFn: ({ loading }: ModuleState) => performHttpRequests && !loading,
      skipLoadedForKeysCheck,
    },
  )

  return {
    callbacks: {
      loadMore,
    },
    canLoadMore,
    filteredBrandGroups,
    filteredBrandGroupsCount,
    hasFilteredBrandGroups,
    loading: loadingBrandGroups,
  }
}

export default useBrandGroups
